import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { CheckCircle, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const POLL_MAX = 10;
const POLL_INTERVAL = 2000;

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState({ status: 'polling', data: null, error: null, attempts: 0 });

  const poll = useCallback(async (attempts) => {
    if (attempts >= POLL_MAX) {
      setState(s => ({ ...s, status: 'timeout' }));
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/payments/checkout/status/${sessionId}`);
      const d = res.data;
      if (d.payment_status === 'paid') {
        setState({ status: 'success', data: d, error: null, attempts });
        return;
      }
      if (d.status === 'expired') {
        setState({ status: 'expired', data: d, error: null, attempts });
        return;
      }
      // Still pending — poll again
      setTimeout(() => poll(attempts + 1), POLL_INTERVAL);
      setState(s => ({ ...s, attempts: attempts + 1 }));
    } catch (e) {
      setState({ status: 'error', data: null, error: e.response?.data?.detail || e.message, attempts });
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error', data: null, error: 'Missing session_id', attempts: 0 });
      return;
    }
    poll(0);
  }, [sessionId, poll]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="subscription-success-page">
      <Header />
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-16 text-center">
        {state.status === 'polling' && (
          <div data-testid="subscription-polling">
            <Loader2 className="h-10 w-10 text-[#E07A3A] mx-auto animate-spin mb-4" />
            <h1 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mb-2">Confirming your payment…</h1>
            <p className="text-sm text-slate-500">This usually takes 5–10 seconds. (Attempt {state.attempts + 1}/{POLL_MAX})</p>
          </div>
        )}

        {state.status === 'success' && (
          <div data-testid="subscription-success">
            <div className="w-16 h-16 bg-[#25D366]/20 border border-[#25D366]/40 mx-auto flex items-center justify-center mb-5">
              <CheckCircle className="h-8 w-8 text-[#25D366]" />
            </div>
            <span className="text-xs tracking-[0.3em] text-[#E07A3A] uppercase font-medium">Payment Successful</span>
            <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0F172A] mt-3 mb-3">
              Welcome to <span className="italic text-[#E07A3A]">{state.data?.tier?.toUpperCase() || 'Shopperden'}</span>
            </h1>
            <p className="text-slate-500 mb-8">
              Your 90-day subscription is now active. Start listing unlimited properties and tap into every Shopperden feature.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/agent?tab=subscription" className="inline-flex items-center justify-center gap-2 bg-[#E07A3A] hover:bg-[#C4652E] text-white px-8 py-3.5 tracking-widest uppercase font-bold text-sm transition-colors" data-testid="go-to-dashboard-btn">
                Open Dashboard <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/agent?tab=my-listings" className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-[#E07A3A] hover:text-[#E07A3A] px-8 py-3.5 tracking-widest uppercase font-bold text-sm transition-colors">
                List a Property
              </Link>
            </div>
          </div>
        )}

        {state.status === 'expired' && (
          <div data-testid="subscription-expired">
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-4" />
            <h1 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mb-2">Checkout session expired</h1>
            <p className="text-sm text-slate-500 mb-6">Don't worry — no charge was made. You can start over.</p>
            <Link to="/agent?tab=subscription" className="inline-flex items-center gap-2 bg-[#E07A3A] text-white px-6 py-3 tracking-widest uppercase font-bold text-xs">Try Again</Link>
          </div>
        )}

        {state.status === 'timeout' && (
          <div data-testid="subscription-timeout">
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-4" />
            <h1 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mb-2">Still confirming…</h1>
            <p className="text-sm text-slate-500 mb-6">
              Stripe is taking longer than usual to confirm. Check your email or refresh this page. If charged, your subscription will activate automatically.
            </p>
            <button onClick={() => window.location.reload()} className="bg-[#E07A3A] text-white px-6 py-3 tracking-widest uppercase font-bold text-xs">Refresh</button>
          </div>
        )}

        {state.status === 'error' && (
          <div data-testid="subscription-error">
            <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-4" />
            <h1 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mb-2">We couldn't confirm your payment</h1>
            <p className="text-sm text-slate-500 mb-6">{state.error}</p>
            <Link to="/agent?tab=subscription" className="inline-flex items-center gap-2 bg-[#E07A3A] text-white px-6 py-3 tracking-widest uppercase font-bold text-xs">Back to Plans</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
